import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import toast from 'react-hot-toast';
import {
  getReportsTargetAchievement,
  getReportsDSO,
  getReportsOverdueAgingBucket,
  addReportsReason,
  getReportsReasons,
  completeMeetingReport,
  getMeetingReport,
  updateManagementPriorities,
  updateFollowUp
} from '../../services/api/implementation/impl';
import { useSmartBack } from '../../../shared/navigation';

// Import section components
import Section1 from './Reports/sections/Section1';
import Section2 from './Reports/sections/Section2';
import Section3 from './Reports/sections/Section3';
import Section4 from './Reports/sections/Section4';
import Section5 from './Reports/sections/Section5';
import Section6 from './Reports/sections/Section6';
import Section7 from './Reports/sections/Section7';
import Section8 from './Reports/sections/Section8';
import Section9 from './Reports/sections/Section9';
import Section10 from './Reports/sections/Section10';

const Reports = ({ mode = 'view', projectID = null, month, priorityId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { onBack } = useSmartBack({ fallback: '/builder/sales/?view=dashboard' });
  const queryClient = useQueryClient();
  const userID = Number(Cookies.get('userId'));

  // Get priority ID from props or location state
  const meetingPriorityId = priorityId || location.state?.priorityId || null;
  const isFollowUpPriority = location.state?.isFollowUpPriority || false; // Flag to indicate if it's a follow-up from SalesDashboard

  // Track which sections are completed and collapsed
  const [completedSections, setCompletedSections] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [activeTab, setActiveTab] = useState('Executive Wise'); // For section 1 tabs
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendance, setAttendance] = useState({}); // { userId: true/false }

  // State for section notes (meeting mode)
  const [sectionNotes, setSectionNotes] = useState({
    targetVsAchievement: [],
    overdueAging: [],
    dso: [],
    visistToBooking: [],
    leadResponseTime: [],
    tat: [],
    cancellation: [],
    accomodation: [],
    helpRequest: [],
    performanceMetrics: []
  });

  // State for meeting report user info (who added the notes)
  const [meetingReportUser, setMeetingReportUser] = useState(null);

  // State for note modal when completing a section
  const [showSectionNoteModal, setShowSectionNoteModal] = useState(false);
  const [selectedSectionForNote, setSelectedSectionForNote] = useState(null);

  // State for reason modal (dynamic for different sections)
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonModalTopic, setReasonModalTopic] = useState(null); // 'DSO', 'Hot Leads Not Converted', 'AR_NOT_CAPTURED', 'Lead Response Time', 'Priority Escalation', etc.
  const [reasonModalLeadId, setReasonModalLeadId] = useState(null);
  const [reasonModalDealId, setReasonModalDealId] = useState(null);
  const [selectedExecutiveForReason, setSelectedExecutiveForReason] = useState(null); // For Section 10
  const [canAddReason, setCanAddReason] = useState(false);
  const [attendees, setAttendees] = useState([]);

  // API Query for Target Achievement
  const {
    data: targetAchievementData,
    isLoading: isLoadingTargetAchievement,
    refetch: refetchTargetAchievement
  } = useQuery({
    queryKey: ['targetAchievement', projectID, month],
    queryFn: () => getReportsTargetAchievement(projectID, month),
    enabled: !!projectID && !!month
  });

  const {
    data: dsoData,
    isLoading: isLoadingDSO,
    refetch: refetchDSO
  } = useQuery({
    queryKey: ['dso', projectID, month],
    queryFn: () => getReportsDSO(projectID, month),
    enabled: !!projectID && !!month
  });

  const {
    data: overdueAgingBucketData,
    isLoading: isLoadingOverdueAgingBucket,
    refetch: refetchOverdueAgingBucket
  } = useQuery({
    queryKey: ['overdueAgingBucket', projectID, month],
    queryFn: () => getReportsOverdueAgingBucket(projectID, month),
    enabled: !!projectID && !!month
  });

  // Fetch all reasons once - sections will filter by topic client-side
  const {
    data: allReasonsData,
    isLoading: isLoadingReasons,
    refetch: refetchAllReasons
  } = useQuery({
    queryKey: ['allReasons', projectID, month],
    queryFn: async () => getReportsReasons(projectID, month),
    enabled: !!projectID && !!month
  });

  // Fetch meeting report data (for meeting mode)
  const {
    data: meetingReportData,
    isLoading: isLoadingMeetingReport,
    refetch: refetchMeetingReport
  } = useQuery({
    queryKey: ['meetingReport', projectID, month],
    queryFn: () => getMeetingReport(projectID, month),
    enabled: !!projectID && !!month
  });

  // Initialize meeting report data when fetched
  useEffect(() => {
    if (
      meetingReportData?.data &&
      Array.isArray(meetingReportData.data) &&
      meetingReportData.data.length > 0
    ) {
      // Get the first meeting report (or handle multiple if needed)
      const data = meetingReportData.data[0];

      // Set completed sections from API data
      setCompletedSections({
        1: data.targetVsAchievement || false,
        2: data.dso || false,
        3: data.overdueAging || false,
        4: data.visistToBooking || false,
        5: data.leadResponseTime || false,
        6: data.tat || false,
        7: data.cancellation || false,
        8: data.accomodation || false,
        9: data.performanceMetrics || false, // Section 9 is now Executive Performance Metrics
        10: data.helpRequest || false // Section 10 is now Sales Manager Help Requests
      });

      // Set section notes from API data
      setSectionNotes({
        targetVsAchievement: data.targetVsAchievementNotes || [],
        overdueAging: data.overdueAgingNotes || [],
        dso: data.dsoNotes || [],
        visistToBooking: data.visistToBookingNotes || [],
        leadResponseTime: data.leadResponseTimeNotes || [],
        tat: data.tatNotes || [],
        cancellation: data.cancellationNotes || [],
        accomodation: data.accomodationNotes || [],
        helpRequest: data.helpRequestNotes || [],
        performanceMetrics: data.performanceMetricsNotes || []
      });

      // Set meeting report user info (who added the notes)
      setMeetingReportUser(data.user || null);

      setAttendees(data.project?.attendeesUsers || []);

      // Set attendance if available
      if (data.presentUser_ids && Array.isArray(data.presentUser_ids)) {
        const presentAttendance = {};
        data.presentUser_ids.forEach((userId) => {
          presentAttendance[userId] = true;
        });
        setAttendance(presentAttendance);
      }
    }
  }, [meetingReportData, mode]);

  useEffect(() => {
    refetchAllReasons();
    refetchTargetAchievement();
    refetchDSO();
    refetchOverdueAgingBucket();
    refetchMeetingReport();
  }, []);

  // Check if user can add reasons (Sales vertical, Medium level, dashboard mode)
  useEffect(() => {
    const userVertical = Cookies.get('userVertical');
    const userDesignationLevel = Cookies.get('userDesignationLevel');

    const canAdd =
      mode === 'dashboard' && userVertical === 'Sales' && userDesignationLevel === 'Medium';

    setCanAddReason(canAdd);
  }, [mode]);

  // Mutation for completing meeting report sections
  const { mutate: completeSection, isPending: isCompletingSection } = useMutation({
    mutationFn: (payload) => completeMeetingReport(payload),
    onSuccess: () => {
      toast.success('Section status updated successfully');
      refetchMeetingReport();
      mixpanel.track('Meeting Section Completed', {
        user_id: userID,
        project_id: projectID,
        month: month,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update section');
      mixpanel.track('Meeting Section Failed', {
        user_id: userID,
        project_id: projectID,
        month: month,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Mutation for adding reasons
  const { mutate: addReason, isPending: isSubmittingReason } = useMutation({
    mutationFn: (payload) => addReportsReason(payload),
    onSuccess: (_, variables) => {
      toast.success('Reason added successfully');
      resetReason();
      setShowReasonModal(false);
      setReasonModalTopic(null);
      setReasonModalLeadId(null);
      setReasonModalDealId(null);
      setSelectedExecutiveForReason(null);
      refetchDSO(); // Refetch DSO data after adding reason
      // Refetch all reasons
      refetchAllReasons();
      mixpanel.track('Reason Added', {
        user_id: userID,
        project_id: projectID,
        month: month,
        topic: variables.topic,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to add reason');
      mixpanel.track('Reason Failed', {
        user_id: userID,
        project_id: projectID,
        month: month,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Form for adding reasons
  const {
    register: registerReason,
    handleSubmit: handleSubmitReason,
    formState: { errors: reasonErrors },
    reset: resetReason
  } = useForm();

  // Form for section notes (when completing a section)
  const {
    register: registerSectionNote,
    handleSubmit: handleSubmitSectionNote,
    formState: { errors: sectionNoteErrors },
    reset: resetSectionNote
  } = useForm();

  // Handle reason form submission (dynamic for different topics)
  const onSubmitReason = (data) => {
    if (!projectID || !month) {
      toast.error('Missing required information');
      return;
    }

    // Determine topic based on context
    let topic = reasonModalTopic;
    if (!topic) {
      // Fallback to old logic for backward compatibility
      topic = 'DSO'; // Default fallback
    }

    const currentYear = new Date().getFullYear();

    const payload = {
      topic: topic,
      reason: data.reason,
      project_id: projectID,
      month: month,
      year: currentYear
    };

    // Add sales_id for "Priority Escalation" topic
    if (topic === 'Priority Escalation') {
      const salesId =
        selectedExecutiveForReason?.sales_id ||
        selectedExecutiveForReason?.id ||
        selectedExecutiveForReason?.user_id ||
        selectedExecutiveForReason?.salesId;
      if (salesId) {
        payload.sales_id = salesId;
      }
    } else {
      // Add lead_id if available (priority: reasonModalLeadId > selectedExecutiveForReason > selectedLeadId)
      const leadId =
        reasonModalLeadId ||
        selectedExecutiveForReason?.lead_id ||
        selectedExecutiveForReason?.id ||
        selectedExecutiveForReason?.leadId;
      if (leadId) {
        payload.lead_id = leadId;
      }
    }

    // Add deal_id if available (for Ar Not Captured and DSO)
    const dealId = reasonModalDealId;
    if (dealId) {
      payload.deal_id = dealId;
    }

    addReason(payload);
  };

  // Handle inline comment submission (for sections with common comments)
  const onSubmitCommentInline = (topic, commentText, reasonId = null) => {
    if (!projectID || !month) {
      toast.error('Missing required information');
      return;
    }

    if (!commentText || !commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    const currentYear = new Date().getFullYear();

    const payload = {
      topic: topic,
      reason: commentText.trim(),
      project_id: projectID,
      month: month,
      year: currentYear
    };

    // Add reason ID if editing existing reason
    if (reasonId) {
      payload.id = reasonId;
    }

    addReason(payload);
  };

  // Handle inline reason submission for table-based reasons (with lead_id, deal_id, or sales_id)
  const onSubmitReasonInline = (
    topic,
    commentText,
    leadId = null,
    dealId = null,
    salesId = null,
    reasonId = null
  ) => {
    if (!projectID || !month) {
      toast.error('Missing required information');
      return;
    }

    if (!commentText || !commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    const currentYear = new Date().getFullYear();

    const payload = {
      topic: topic,
      reason: commentText.trim(),
      project_id: projectID,
      month: month,
      year: currentYear
    };

    // Add reason ID if editing existing reason
    if (reasonId) {
      payload.id = reasonId;
    }

    // Add sales_id for "Priority Escalation" topic
    if (topic === 'Priority Escalation' && salesId) {
      payload.sales_id = salesId;
    } else if (leadId) {
      // Add lead_id for other topics
      payload.lead_id = leadId;
    }

    // Add deal_id if available (for Cancelled Deal Reason, Ar Not Captured, etc.)
    if (dealId) {
      payload.deal_id = dealId;
    }

    addReason(payload);
  };

  // Handle opening reason modal for different sections
  const handleOpenReasonModal = (topic, leadId = null, dealId = null, executiveData = null) => {
    setReasonModalTopic(topic);
    setReasonModalLeadId(leadId);
    setReasonModalDealId(dealId);
    setSelectedExecutiveForReason(executiveData); // Store executive data for Section 10
    setShowReasonModal(true);
    mixpanel.track('Reason Modal Opened', {
      user_id: userID,
      topic: topic,
      lead_id: leadId,
      timestamp: new Date().toISOString()
    });
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Review Dashboard Viewed', {
      user_id: userID,
      mode: mode,
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  }, [userID, mode, projectID]);

  // Check if all sections completed and show attendance modal (meeting mode only)
  useEffect(() => {
    if (mode === 'meeting') {
      const completedCount = Object.values(completedSections).filter(Boolean).length;
      if (completedCount === 10 && !showAttendanceModal) {
        setShowAttendanceModal(true);
      }
    }
  }, [completedSections, mode, showAttendanceModal]);

  // Map section number to section title
  const getSectionTitle = (sectionNumber) => {
    const sectionTitles = {
      1: 'Target vs Achievement',
      2: 'Days Sales Outstanding (DSO)',
      3: 'Overdue Aging Bucket',
      4: 'Visits to Booking Conversion',
      5: 'Lead Response Time',
      6: 'Turnaround Time',
      7: 'Cancellations',
      8: 'Accommodation/Plot Area Mix',
      9: 'Executive Performance Metrics',
      10: 'Sales Manager Help Requests'
    };
    return sectionTitles[sectionNumber] || `Section ${sectionNumber}`;
  };

  // Map section number to payload key
  const getSectionPayloadKey = (sectionNumber) => {
    const mapping = {
      1: 'targetVsAchievement',
      2: 'dso',
      3: 'overdueAging',
      4: 'visistToBooking',
      5: 'leadResponseTime',
      6: 'tat',
      7: 'cancellation',
      8: 'accomodation',
      9: 'performanceMetrics', // Section 9 is now Executive Performance Metrics
      10: 'helpRequest' // Section 10 is now Sales Manager Help Requests
    };
    return mapping[sectionNumber];
  };

  // Map section number to notes key
  const getSectionNotesKey = (sectionNumber) => {
    const mapping = {
      1: 'targetVsAchievement',
      2: 'dso',
      3: 'overdueAging',
      4: 'visistToBooking',
      5: 'leadResponseTime',
      6: 'tat',
      7: 'cancellation',
      8: 'accomodation',
      9: 'performanceMetrics', // Section 9 is now Executive Performance Metrics
      10: 'helpRequest' // Section 10 is now Sales Manager Help Requests
    };
    return mapping[sectionNumber];
  };

  // Build payload for completeMeetingReport (with optional override for completed sections)
  const buildMeetingReportPayload = (overrideCompletedSections = null) => {
    const currentYear = new Date().getFullYear();

    // Use override if provided, otherwise use state
    const completed = overrideCompletedSections || completedSections;

    // Get all user IDs from the attendees list
    const allUserIds = attendees?.map((user) => parseInt(user.id)) || [];

    // Get present user IDs (marked as present)
    const presentUserIds = allUserIds.filter((userId) => attendance[userId] === true);

    // Get absent user IDs (not marked as present)
    const absentUserIds = allUserIds.filter((userId) => attendance[userId] !== true);

    return {
      project_id: projectID,
      month: month,
      year: currentYear,
      targetVsAchievement: completed[1] || false,
      dso: completed[2] || false,
      overdueAging: completed[3] || false,
      visistToBooking: completed[4] || false,
      leadResponseTime: completed[5] || false,
      tat: completed[6] || false,
      cancellation: completed[7] || false,
      accomodation: completed[8] || false,
      performanceMetrics: completed[9] || false, // Section 9 is now Executive Performance Metrics
      helpRequest: completed[10] || false, // Section 10 is now Sales Manager Help Requests
      presentUser_ids: presentUserIds,
      absentUser_ids: absentUserIds,
      targetVsAchievementNotes: sectionNotes.targetVsAchievement,
      overdueAgingNotes: sectionNotes.overdueAging,
      dsoNotes: sectionNotes.dso,
      visistToBookingNotes: sectionNotes.visistToBooking,
      leadResponseTimeNotes: sectionNotes.leadResponseTime,
      tatNotes: sectionNotes.tat,
      cancellationNotes: sectionNotes.cancellation,
      accomodationNotes: sectionNotes.accomodation,
      helpRequestNotes: sectionNotes.helpRequest,
      performanceMetricsNotes: sectionNotes.performanceMetrics
    };
  };

  const toggleSectionCompleted = (sectionNumber) => {
    if (mode === 'view') return; // No action in view mode

    const newCompletedState = !completedSections[sectionNumber];

    // Update local state immediately
    const updatedCompletedSections = {
      ...completedSections,
      [sectionNumber]: newCompletedState
    };
    setCompletedSections(updatedCompletedSections);

    // If completing (checking), show note modal
    if (newCompletedState && mode === 'meeting') {
      setSelectedSectionForNote(sectionNumber);
      setShowSectionNoteModal(true);
    } else if (mode === 'meeting') {
      // If unchecking, call API immediately with updated state
      const payload = buildMeetingReportPayload(updatedCompletedSections);
      completeSection(payload);
    }

    mixpanel.track('Section Toggled', {
      user_id: userID,
      section: sectionNumber,
      completed: newCompletedState,
      mode: mode,
      timestamp: new Date().toISOString()
    });
  };

  // Handle section note submission
  const onSubmitSectionNote = (data) => {
    if (!selectedSectionForNote) return;

    const notesKey = getSectionNotesKey(selectedSectionForNote);
    const noteText = data.note?.trim();

    // Validate that note is not empty
    if (!noteText) {
      toast.error('Please enter a note before submitting');
      return;
    }

    // Update notes state if note provided (note is optional, so empty string is fine)
    let updatedNotes = { ...sectionNotes };
    if (noteText) {
      updatedNotes[notesKey] = [...(sectionNotes[notesKey] || []), noteText];
      setSectionNotes(updatedNotes);
    }

    // Close modal
    setShowSectionNoteModal(false);
    setSelectedSectionForNote(null);
    resetSectionNote();

    // Call API with updated payload (use updated notes, even if empty)
    const payload = buildMeetingReportPayload();
    // Override notes in payload with updated notes (will be empty array if no note was added)
    payload[`${notesKey}Notes`] = updatedNotes[notesKey] || [];
    completeSection(payload);
  };

  const toggleSectionCollapse = (sectionNumber) => {
    if (mode === 'view') return; // No collapse in view mode

    setCollapsedSections((prev) => ({
      ...prev,
      [sectionNumber]: !prev[sectionNumber]
    }));
  };

  const handleAttendanceToggle = (userId) => {
    setAttendance((prev) => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Mutation for updating management priority
  const { mutate: updatePriorityMutation, isPending: isUpdatingPriority } = useMutation({
    mutationFn: ({ id, payload }) => updateManagementPriorities(id, payload),
    onSuccess: () => {
      toast.success('Meeting completed successfully');
      mixpanel.track('Meeting Priority Updated', {
        user_id: userID,
        priority_id: meetingPriorityId,
        project_id: projectID,
        timestamp: new Date().toISOString()
      });
      // Navigate back to dashboard
      navigate('/builder/dashboard');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to complete meeting');
      mixpanel.track('Meeting Priority Update Failed', {
        user_id: userID,
        priority_id: meetingPriorityId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Mutation for updating follow-up status (for SalesDashboard)
  const { mutate: updateFollowUpMutation, isPending: isUpdatingFollowUp } = useMutation({
    mutationFn: ({ id, payload }) => updateFollowUp(id, payload),
    onSuccess: () => {
      toast.success('Report review completed successfully');
      mixpanel.track('Follow Up Updated', {
        user_id: userID,
        follow_up_id: meetingPriorityId,
        project_id: projectID,
        timestamp: new Date().toISOString()
      });
      // Navigate back to dashboard
      navigate('/builder/dashboard');
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to complete report review'
      );
      mixpanel.track('Follow Up Update Failed', {
        user_id: userID,
        follow_up_id: meetingPriorityId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Handle complete button click (for dashboard mode)
  const handleCompleteClick = () => {
    if (!meetingPriorityId) {
      toast.error('Priority ID not found');
      return;
    }

    // If coming from SalesDashboard (follow-up), use updateFollowUp API
    if (isFollowUpPriority) {
      updateFollowUpMutation({
        id: meetingPriorityId,
        payload: { status: 'Inactive' }
      });
    }
  };

  const handleAttendanceSubmit = () => {
    if (!meetingPriorityId) {
      toast.error('Priority ID not found');
      return;
    }

    // Build final payload with attendance
    const payload = buildMeetingReportPayload();

    // Call API to save meeting report with attendance
    completeSection(payload);

    // Update management priority with status completed
    updatePriorityMutation({
      id: meetingPriorityId,
      payload: { status: 'Completed' }
    });

    // Track attendance submission
    mixpanel.track('Attendance Submitted', {
      user_id: userID,
      attendance_count: payload.presentUser_ids.length,
      absent_count: payload.absentUser_ids.length,
      total_attendees: attendees?.length || 0,
      timestamp: new Date().toISOString()
    });

    // Close modal (navigation will happen in mutation success)
    setShowAttendanceModal(false);
  };

  // Calculate review progress
  const completedCount = Object.values(completedSections).filter(Boolean).length;
  const progressPercentage = (completedCount / 10) * 100;

  // Determine if actions are allowed (dashboard and meeting modes)
  const allowActions = mode === 'dashboard' || mode === 'meeting';

  const renderSectionHeader = (sectionNumber, title) => {
    const showCheckbox = mode === 'meeting';
    const canInteract = mode !== 'view';

    return (
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">
          {sectionNumber}. {title}
        </h2>
        <div className="flex items-center gap-2">
          {canInteract && (
            <button
              onClick={() => toggleSectionCollapse(sectionNumber)}
              className="p-1 hover:bg-gray-100 rounded transition-colors">
              <svg
                className={`w-5 h-5 text-gray-600 transition-transform ${collapsedSections[sectionNumber] ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          )}
          {showCheckbox && (
            <button
              onClick={() => toggleSectionCompleted(sectionNumber)}
              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                completedSections[sectionNumber]
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-300 text-gray-400 hover:border-blue-400'
              }`}>
              {completedSections[sectionNumber] && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Container class based on mode
  const containerClass =
    mode === 'meeting'
      ? 'w-full h-full fixed inset-0 z-50 bg-white overflow-y-auto'
      : mode === 'view'
        ? 'w-full h-full mt-[13rem]'
        : mode === 'viewOnly'
          ? 'w-full h-full mt-[5.5rem]'
          : 'w-full h-full mt-[5.5rem]';

  return (
    <div className={containerClass}>
      <Loader
        isVisible={
          isLoadingTargetAchievement ||
          isLoadingDSO ||
          isLoadingOverdueAgingBucket ||
          isLoadingMeetingReport ||
          isCompletingSection ||
          isSubmittingReason ||
          isUpdatingPriority ||
          isUpdatingFollowUp
        }
      />

      {/* Header - Only show for meeting mode */}
      {mode === 'meeting' && (
        <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between rounded-lg bg-white">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                <svg
                  className="w-6 h-6 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Sales Review Dashboard</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Interactive meeting agenda and insights
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-sm font-medium text-gray-600 mb-1">Review Progress</span>
              <span className="text-2xl font-bold text-gray-900">{completedCount}/10</span>
              <div className="w-48 h-2 bg-gray-200 rounded-full mt-2">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`p-6 space-y-6 ${mode === 'meeting' ? 'pt-6' : mode === 'view' ? 'pt-6' : 'pt-0'}`}>
        {/* Section 1: Target vs Achievement */}
        <Section1
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          targetAchievementData={targetAchievementData}
          isLoadingTargetAchievement={isLoadingTargetAchievement}
          projectID={projectID}
          allowActions={allowActions}
          month={month}
          canAddReason={canAddReason}
          allReasonsData={allReasonsData?.data || []}
          isLoadingReasons={isLoadingReasons}
          handleOpenReasonModal={handleOpenReasonModal}
          onSubmitReasonInline={onSubmitReasonInline}
          isSubmittingReason={isSubmittingReason}
          sectionNote={
            sectionNotes.targetVsAchievement?.[0]
              ? {
                  note: sectionNotes.targetVsAchievement[0],
                  user: meetingReportUser
                }
              : null
          }
        />

        {/* Section 2: Days Sales Outstanding */}
        <Section2
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          dsoData={dsoData}
          allReasonsData={allReasonsData?.data || []}
          isLoadingReasons={isLoadingReasons}
          allowActions={allowActions}
          handleOpenReasonModal={handleOpenReasonModal}
          canAddReason={canAddReason}
          onSubmitCommentInline={onSubmitCommentInline}
          isSubmittingComment={isSubmittingReason}
          sectionNote={
            sectionNotes.dso?.[0]
              ? {
                  note: sectionNotes.dso[0],
                  user: meetingReportUser
                }
              : null
          }
        />

        {/* Section 3: Overdue Aging Bucket */}
        <Section3
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          overdueAgingBucketData={overdueAgingBucketData}
          allReasonsData={allReasonsData?.data || []}
          isLoadingReasons={isLoadingReasons}
          handleOpenReasonModal={handleOpenReasonModal}
          canAddReason={canAddReason}
          onSubmitReasonInline={onSubmitReasonInline}
          isSubmittingReason={isSubmittingReason}
          sectionNote={
            sectionNotes.overdueAging?.[0]
              ? {
                  note: sectionNotes.overdueAging[0],
                  user: meetingReportUser
                }
              : null
          }
        />

        {/* Section 4: Visits to Booking Conversion */}
        <Section4
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          allReasonsData={allReasonsData?.data || []}
          isLoadingReasons={isLoadingReasons}
          handleOpenReasonModal={handleOpenReasonModal}
          dsoData={dsoData}
          month={month}
          canAddReason={canAddReason}
          onSubmitCommentInline={onSubmitCommentInline}
          isSubmittingComment={isSubmittingReason}
          sectionNote={
            sectionNotes.visistToBooking?.[0]
              ? {
                  note: sectionNotes.visistToBooking[0],
                  user: meetingReportUser
                }
              : null
          }
        />

        {/* Section 5: Lead Response Time */}
        <Section5
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          allReasonsData={allReasonsData?.data || []}
          isLoadingReasons={isLoadingReasons}
          handleOpenReasonModal={handleOpenReasonModal}
          dsoData={dsoData}
          canAddReason={canAddReason}
          onSubmitCommentInline={onSubmitCommentInline}
          isSubmittingComment={isSubmittingReason}
          sectionNote={
            sectionNotes.leadResponseTime?.[0]
              ? {
                  note: sectionNotes.leadResponseTime[0],
                  user: meetingReportUser
                }
              : null
          }
        />

        {/* Section 6: Turnaround Time */}
        <Section6
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          dsoData={dsoData}
          sectionNote={
            sectionNotes.tat?.[0]
              ? {
                  note: sectionNotes.tat[0],
                  user: meetingReportUser
                }
              : null
          }
        />

        {/* Section 7: Cancellations */}
        <Section7
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          dsoData={dsoData}
          allReasonsData={allReasonsData?.data || []}
          isLoadingReasons={isLoadingReasons}
          handleOpenReasonModal={handleOpenReasonModal}
          canAddReason={canAddReason}
          onSubmitReasonInline={onSubmitReasonInline}
          isSubmittingReason={isSubmittingReason}
          sectionNote={
            sectionNotes.cancellation?.[0]
              ? {
                  note: sectionNotes.cancellation[0],
                  user: meetingReportUser
                }
              : null
          }
        />

        {/* Section 8: Accommodation/Plot Area Mix */}
        <Section8
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={renderSectionHeader}
          dsoData={dsoData}
          sectionNote={
            sectionNotes.accomodation?.[0]
              ? {
                  note: sectionNotes.accomodation[0],
                  user: meetingReportUser
                }
              : null
          }
        />
        {/* Section 9: Executive Performance Metrics */}
        <Section10
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={(sectionNumber, title) => renderSectionHeader(9, title)}
          dsoData={dsoData}
          allReasonsData={allReasonsData?.data || []}
          isLoadingReasons={isLoadingReasons}
          handleOpenReasonModal={handleOpenReasonModal}
          canAddReason={canAddReason}
          onSubmitReasonInline={onSubmitReasonInline}
          isSubmittingReason={isSubmittingReason}
          sectionNote={
            sectionNotes.performanceMetrics?.[0]
              ? {
                  note: sectionNotes.performanceMetrics[0],
                  user: meetingReportUser
                }
              : null
          }
        />
        {/* Section 10: Sales Manager Help Requests */}
        <Section9
          mode={mode}
          collapsedSections={collapsedSections}
          renderSectionHeader={(sectionNumber, title) => renderSectionHeader(10, title)}
          projectID={projectID}
          month={month}
          canAddReason={canAddReason}
          sectionNote={
            sectionNotes.helpRequest?.[0]
              ? {
                  note: sectionNotes.helpRequest[0],
                  user: meetingReportUser
                }
              : null
          }
        />
      </div>

      {/* Attendance Modal - Only for meeting mode */}
      {mode === 'meeting' && showAttendanceModal && (
        <Modal name="Mark Attendance" width={50} onClose={() => setShowAttendanceModal(false)}>
          <div className="">
            <p className="text-sm text-gray-600 mb-6">
              Select all attendees who are present for this meeting.
            </p>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {attendees?.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={attendance[user.id] || false}
                      onChange={() => handleAttendanceToggle(user.id)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <Button
                onClick={handleAttendanceSubmit}
                disabled={isCompletingSection || isUpdatingPriority}
                className="bg-primary text-white hover:bg-primary/90">
                {isCompletingSection || isUpdatingPriority ? 'Submitting...' : 'Submit Attendance'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Section Note Modal - for adding notes when completing a section */}
      {showSectionNoteModal && (
        <Modal
          name={`Add Note for ${getSectionTitle(selectedSectionForNote)}`}
          width={50}
          hideClose={true}
          onClose={() => {
            setShowSectionNoteModal(false);
            setSelectedSectionForNote(null);
            resetSectionNote();
          }}>
          <form onSubmit={handleSubmitSectionNote(onSubmitSectionNote)}>
            <div className="">
              <FormInput
                width={100}
                label="Note (Optional)"
                register={registerSectionNote}
                register_key="note"
                type="textarea"
                isRequired={false}
                placeholder="Enter your note here..."
                errors={sectionNoteErrors}
              />
              <div className="flex justify-end gap-3 mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    // Close modal
                    setShowSectionNoteModal(false);
                    setSelectedSectionForNote(null);
                    resetSectionNote();

                    // Call API with current state (no note added)
                    const payload = buildMeetingReportPayload();
                    completeSection(payload);
                  }}
                  displayType="secondary">
                  Skip
                </Button>
                <Button type="submit" disabled={isCompletingSection}>
                  {isCompletingSection ? 'Submitting...' : 'Add Note'}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Dynamic Comment Modal - for all sections */}
      {showReasonModal && (
        <Modal
          name={`Add Comment${reasonModalTopic ? ` - ${reasonModalTopic}` : ''}`}
          width={50}
          onClose={() => {
            setShowReasonModal(false);
            setReasonModalTopic(null);
            setReasonModalLeadId(null);
            setReasonModalDealId(null);
            setSelectedExecutiveForReason(null);
            resetReason();
          }}>
          <div className="">
            <form onSubmit={handleSubmitReason(onSubmitReason)}>
              <FormInput
                width={100}
                label="Comment"
                register={registerReason}
                register_key="reason"
                isRequired={true}
                type="textarea"
                validation={{ required: 'Comment is required' }}
                errors={reasonErrors}
              />
              <div className="flex justify-end gap-3 mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowReasonModal(false);
                    setReasonModalTopic(null);
                    setReasonModalLeadId(null);
                    setReasonModalDealId(null);
                    setSelectedExecutiveForReason(null);
                    resetReason();
                  }}
                  className="bg-gray-200 text-gray-700 hover:bg-gray-300"
                  displayType="secondary">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmittingReason || !projectID || !month}>
                  {isSubmittingReason ? 'Submitting...' : 'Submit Comment'}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Floating Complete Button - Only for dashboard mode when can add reasons */}
      {mode === 'dashboard' && canAddReason && meetingPriorityId && (
        <div className="fixed bottom-8 right-8 z-50">
          <Button
            onClick={handleCompleteClick}
            disabled={isUpdatingPriority || isUpdatingFollowUp}
            className="bg-primary text-white hover:bg-primary/90 shadow-lg px-6 py-3 rounded-lg font-semibold text-base">
            {isUpdatingPriority || isUpdatingFollowUp ? 'Completing...' : 'Complete'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Reports;
